/**
 * Rolling 7-day shortcut usage for the signed-in home screen.
 * Keys match primary destinations (bottom nav + hub tiles).
 */

import { safeParseJson } from '../utils/safeParseJson';

export type HomeShortcutKey = 'plan' | 'grade' | 'teach' | 'schedule' | 'communicate' | 'options';

const STORAGE_KEY = 'dg_shortcut_usage_v1';
const LEGACY_NAV_KEY = 'dg_nav_usage_v1';

export type ShortcutUsageStore = {
  v: 1;
  /** Local calendar date YYYY-MM-DD → per-shortcut counts that day */
  days: Record<string, Partial<Record<HomeShortcutKey, number>>>;
};

export const DEFAULT_SHORTCUT_ORDER: HomeShortcutKey[] = [
  'schedule',
  'grade',
  'teach',
  'plan',
  'communicate',
  'options',
];

function localYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Last N calendar dates ending at `end` (inclusive), oldest first. */
function lastNDates(end: Date, n: number): string[] {
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(end);
    d.setDate(d.getDate() - i);
    out.push(localYMD(d));
  }
  return out;
}

export function emptyShortcutStore(): ShortcutUsageStore {
  return { v: 1, days: {} };
}

/** Load from localStorage; migrate legacy totals into today's bucket once. */
export function loadShortcutUsageStore(): ShortcutUsageStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = safeParseJson<Partial<ShortcutUsageStore> | null>(raw, null);
      if (parsed?.v === 1 && parsed.days && typeof parsed.days === 'object') {
        return { v: 1, days: { ...parsed.days } };
      }
    }
  } catch {
    /* ignore */
  }

  const store = emptyShortcutStore();
  const today = localYMD(new Date());
  try {
    const leg = localStorage.getItem(LEGACY_NAV_KEY);
    if (leg) {
      const o = safeParseJson<Record<string, number>>(leg, {});
      const mapLegacy = (k: string): HomeShortcutKey | null => {
        if (
          k === 'plan' ||
          k === 'grade' ||
          k === 'teach' ||
          k === 'communicate' ||
          k === 'schedule'
        )
          return k;
        return null;
      };
      for (const [k, n] of Object.entries(o)) {
        const sk = mapLegacy(k);
        if (!sk || typeof n !== 'number' || n <= 0) continue;
        store.days[today] = store.days[today] || {};
        store.days[today]![sk] = (store.days[today]![sk] || 0) + Math.min(n, 500);
      }
    }
  } catch {
    /* ignore */
  }
  return store;
}

export function saveShortcutUsageStore(store: ShortcutUsageStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore */
  }
}

/** Drop day keys older than 7 days before today (keep today + 6 prior = 7 rolling). */
export function pruneShortcutUsageStore(
  store: ShortcutUsageStore,
  now = new Date()
): ShortcutUsageStore {
  const keep = new Set(lastNDates(now, 7));
  const next: Record<string, Partial<Record<HomeShortcutKey, number>>> = {};
  for (const [day, counts] of Object.entries(store.days)) {
    if (keep.has(day)) next[day] = counts;
  }
  return { v: 1, days: next };
}

export function bumpShortcutUsage(
  store: ShortcutUsageStore,
  key: HomeShortcutKey,
  now = new Date()
): ShortcutUsageStore {
  const pruned = pruneShortcutUsageStore(store, now);
  const day = localYMD(now);
  const days = { ...pruned.days };
  days[day] = { ...days[day], [key]: (days[day]?.[key] || 0) + 1 };
  return { v: 1, days };
}

export function aggregateShortcutUsage(
  store: ShortcutUsageStore,
  now = new Date()
): Record<HomeShortcutKey, number> {
  const pruned = pruneShortcutUsageStore(store, now);
  const totals: Record<HomeShortcutKey, number> = {
    plan: 0,
    grade: 0,
    teach: 0,
    schedule: 0,
    communicate: 0,
    options: 0,
  };
  for (const counts of Object.values(pruned.days)) {
    if (!counts) continue;
    (Object.keys(totals) as HomeShortcutKey[]).forEach((k) => {
      totals[k] += counts[k] || 0;
    });
  }
  return totals;
}

/**
 * Pick up to `limit` shortcuts: most-used in the last 7 days, then defaults for any slots left.
 */
export function rankHomeShortcuts(
  store: ShortcutUsageStore,
  limit = 4,
  now = new Date()
): HomeShortcutKey[] {
  const totals = aggregateShortcutUsage(store, now);
  const ranked = (Object.entries(totals) as [HomeShortcutKey, number][]).sort(
    (a, b) =>
      b[1] - a[1] || DEFAULT_SHORTCUT_ORDER.indexOf(a[0]) - DEFAULT_SHORTCUT_ORDER.indexOf(b[0])
  );

  const out: HomeShortcutKey[] = [];
  const used = new Set<HomeShortcutKey>();
  for (const [k] of ranked) {
    if (out.length >= limit) break;
    if (used.has(k)) continue;
    used.add(k);
    out.push(k);
  }
  for (const k of DEFAULT_SHORTCUT_ORDER) {
    if (out.length >= limit) break;
    if (!used.has(k)) {
      used.add(k);
      out.push(k);
    }
  }
  return out.slice(0, limit);
}
